import { useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { clearUserInfo } from "@/store/modules/user";

/**
 * 增强版 IdleTimer
 * 1. 监控原生 UI 事件 (点击、键盘)
 * 2. 监控自定义业务事件 (app-active)
 * 3. 🌟 新增：全局拦截 Fetch 请求 (只要有 API 往来就视为活跃)
 */
export default function IdleTimer() {
  const dispatch = useDispatch();
  
  // 使用 Ref 避免 React 渲染干扰
  const lastActivity = useRef(Date.now());
  const timerInstance = useRef(null);

  // --- 配置区域 ---
  const TIMEOUT_LIMIT = 18000000; // 3分钟 (180,000ms)
  const CHECK_INTERVAL = 5000;  // 每5秒检查一次，提高响应精度

  useEffect(() => {
    // console.log("🛠️ [IdleTimer] 监控已启动。当前超时限制: 3分钟");

    // 重置活跃时间函数
    const updateActivity = () => {
      // lastActivity.current = Date.now();
      const now = Date.now() + 1000; // 加1秒，给用户一点操作缓冲时间，避免刚好3分钟就过期的尴尬
      lastActivity.current = now;
      // 新增：存入本地存储，以便 UI 组件读取
      localStorage.setItem('lastActivity', now.toString());

    };

    // 初始化时先执行一次，确保 localStorage 有值
    updateActivity();

    // --- 1. 绑定原生 UI 事件 ---
    const uiEvents = ["mousedown", "keydown", "scroll", "touchstart", "mousemove"];
    uiEvents.forEach(event => window.addEventListener(event, updateActivity));

    // --- 2. 绑定自定义事件 (作为手动补丁) ---
    window.addEventListener("app-active", updateActivity);

    // --- 3. 🌟 全局 Fetch 拦截器 ---
    // 只要代码里执行了 fetch()，就自动视为用户活跃，重置倒计时
    const originalFetch = window.fetch;
    window.fetch = async (...args) => {
      updateActivity(); // 拦截请求，更新时间
      return originalFetch(...args);
    };

    // --- 4. 核心检查轮询 ---
    timerInstance.current = setInterval(() => {
      // 登录页面不执行检查
      if (window.location.pathname.includes('/login')) {
        updateActivity(); // 在登录页时持续刷新时间，防止一登录就过期
        return;
      }
      
      const now = Date.now();
      const diff = now - lastActivity.current;
      const secondsInIdle = Math.floor(diff / 1000);

      // 调试用：可以在控制台看到静止秒数
      // console.log(`⏱️ [IdleTimer] 已经静止: ${secondsInIdle} 秒`);
    
      if (diff > TIMEOUT_LIMIT) {
        // 达到超时限制
        if (timerInstance.current) clearInterval(timerInstance.current);
        
        // 执行登出流程
        handleLogout();
      }
    }, CHECK_INTERVAL);

    // --- 5. 清理函数 ---
    return () => {
      uiEvents.forEach(event => window.removeEventListener(event, updateActivity));
      window.removeEventListener("app-active", updateActivity);
      window.fetch = originalFetch; // 恢复原始 fetch 函数，防止内存泄漏
      if (timerInstance.current) clearInterval(timerInstance.current);
    };
  }, [dispatch]);

  // 登出逻辑封装
  const handleLogout = () => {
    showExpiredMessage();
    
    // 清理 Redux 和持久化存储
    dispatch(clearUserInfo());
    localStorage.clear();
    sessionStorage.clear();
    
    // 1.5秒后跳转，留出显示提示框的时间
    setTimeout(() => {
      window.location.href = "/login";
    }, 1500);
  };

  return null;
}

/**
 * 覆盖全屏的过期提示
 */
function showExpiredMessage() {
  if (document.getElementById('session-expired-overlay')) return;

  const div = document.createElement("div");
  div.id = 'session-expired-overlay';
  div.style.cssText = `
    position: fixed; top: 0; left: 0; width: 100vw; height: 100vh;
    z-index: 999999; background: rgba(0, 0, 0, 0.7);
    backdrop-filter: blur(5px); display: flex; align-items: center; justify-content: center;
  `;
  
  div.innerHTML = `
    <div style="
      background: white; padding: 30px; border-radius: 12px;
      text-align: center; box-shadow: 0 20px 40px rgba(0,0,0,0.3);
      border-top: 6px solid #ff4d4f; min-width: 300px;
    ">
      <div style="font-size: 40px; margin-bottom: 10px;">⏰</div>
      <h2 style="margin: 0; color: #333; font-size: 20px;">Session Expired</h2>
      <p style="margin: 15px 0; color: #666;">You have been inactive for over 3 minutes.</p>
      <div style="color: #999; font-size: 13px;">Redirecting to login page...</div>
    </div>
  `;
  
  document.body.appendChild(div);
}